import { Aviso, Cartao, Celula, Chip, Corpo, Linha, Tabela } from "../componentes/ui";
import {
  CAMINHO_DO_ARQUIVO_DO_CONTRATO,
  PERMISSAO_DA_TELA_CONTRATO,
  caminhoDoTermoDeEncerramento,
  linhasDoEndereco,
  situacaoDoItem,
  useMeuContrato,
  useTermosDeEncerramento,
  type ContratoDoCliente as DadosDoContrato,
  type ItemContratado,
} from "../nucleo/contratoDoCliente";
import { rotuloDoAmbiente } from "../nucleo/console";
import { formatarCnpj, formatarData } from "../nucleo/implantacoes";
import { formatarTamanho } from "../nucleo/reuniao";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

/**
 * A tela Contrato do cliente — item 10 da Amanda e tela 8 do protótipo v9.
 *
 * O cliente confere aqui o que foi contratado e o arquivo que assinou. O conteúdo é o do cadastro,
 * não um resumo escrito à mão: divergência entre esta tela e o contrato é erro de cadastro, e
 * aparece rápido justamente porque o cliente olha (legenda do protótipo).
 *
 * **Visibilidade.** `contrato:portal` é a tela "Contrato" da matriz de acesso. O menu esconde o
 * item e esta tela repete a conferência para quem digita o endereço; o servidor nega de
 * qualquer jeito. Nada é pedido sem a permissão, para não colher um 403 mostrado como erro.
 *
 * **Termos de encerramento.** Quem os gera é o repasse de módulo (outra frente). Enquanto a rota
 * deles não existe a seção mostra o estado vazio — não esconde a seção: o cliente precisa saber
 * que o termo vai ficar aqui.
 */
export function ContratoDoCliente() {
  useTitulo("Contrato");
  const sessao = useSessao();
  const pode = podeNaSessao(sessao.data, PERMISSAO_DA_TELA_CONTRATO);
  const contrato = useMeuContrato(pode);

  if (sessao.data && !pode) {
    return (
      <div data-testid="contrato-do-cliente">
        <h1 className="titulo-pagina">Contrato</h1>
        <Aviso tom="marca" data-testid="contrato-sem-permissao">
          <b>O seu acesso não inclui o contrato.</b> Quem administra o acesso da sua empresa pode
          liberá-lo.
        </Aviso>
      </div>
    );
  }

  return (
    <div data-testid="contrato-do-cliente">
      <h1 className="titulo-pagina">Seu contrato</h1>
      <p className="lead">O que foi contratado, e o arquivo assinado.</p>
      {!sessao.data || contrato.isPending ? (
        <p className="lead" role="status">
          Carregando o seu contrato…
        </p>
      ) : contrato.isError ? (
        <Aviso tom="erro" data-testid="falha-do-contrato-do-cliente">
          Não foi possível carregar o seu contrato agora. Atualize a página em alguns instantes; se
          continuar, fale com o consultor responsável.
        </Aviso>
      ) : contrato.data ? (
        <Conteudo dados={contrato.data} />
      ) : (
        <Aviso tom="marca" data-testid="sem-contrato-registrado">
          <b>Assim que o time comercial registrar o contrato</b>, ele aparece aqui com as datas, os
          módulos e o arquivo assinado.
        </Aviso>
      )}
    </div>
  );
}

function Conteudo({ dados }: { dados: DadosDoContrato }) {
  return (
    <>
      <Datas dados={dados} />
      <div className="grid gap-3 md:grid-cols-2 mb-3">
        <Escopo
          titulo="Módulos contratados"
          testid="modulos-contratados"
          itens={dados.modulos}
          vazio="Nenhum módulo registrado."
        />
        <Escopo
          titulo="Recursos Plus contratados"
          testid="recursos-contratados"
          itens={dados.recursos_plus}
          vazio="Nenhum Recurso Plus contratado."
          modulos={dados.modulos}
        />
      </div>
      <Empresa dados={dados} />
      <Documentos dados={dados} />
    </>
  );
}

function Dado({ rotulo, children, ...resto }: { rotulo: string; children: React.ReactNode; "data-testid"?: string }) {
  return (
    <div>
      <dt className="campo__rotulo">{rotulo}</dt>
      <dd className="m-0 text-sm" {...resto}>
        {children}
      </dd>
    </div>
  );
}

function Datas({ dados }: { dados: DadosDoContrato }) {
  const entrega = dados.relogio_iniciado ? formatarData(dados.entrega_prevista) : null;
  return (
    <section className="mb-3" aria-label="Datas do contrato">
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 m-0" data-testid="datas-do-contrato">
        <Cartao>
          <Dado rotulo="Assinatura" data-testid="data-da-assinatura">
            <span className="numero-tabular">{formatarData(dados.contrato_assinado_em) ?? "—"}</span>
          </Dado>
        </Cartao>
        <Cartao>
          <Dado rotulo="Prazo contratado" data-testid="prazo-contratado">
            <span className="numero-tabular">{dados.prazo_dias}</span> dias
          </Dado>
        </Cartao>
        <Cartao>
          <Dado rotulo="Ambiente liberado" data-testid="data-do-ambiente">
            <span className="numero-tabular">
              {formatarData(dados.ambiente_liberado_em) ?? "Aguardando a GEOvendas"}
            </span>
          </Dado>
        </Cartao>
        <Cartao>
          <Dado rotulo="Aceite do kickoff" data-testid="data-do-kickoff">
            <span className="numero-tabular">
              {formatarData(dados.kickoff_aceito_em) ?? "Aguardando o aceite"}
            </span>
          </Dado>
        </Cartao>
        <Cartao>
          <Dado rotulo="Entrega prevista" data-testid="data-da-entrega">
            <span className="numero-tabular">{entrega ?? "A definir"}</span>
          </Dado>
        </Cartao>
      </dl>
      {entrega ? null : (
        <p className="nota-rodape mt-2" data-testid="explicacao-da-entrega">
          Os {dados.prazo_dias} dias do contrato começam após a liberação do ambiente e a geração da tarefa de
          kickoff; a data de entrega aparece aqui quando os dois acontecem.
        </p>
      )}
    </section>
  );
}

function Escopo({
  titulo,
  testid,
  itens,
  vazio,
  modulos,
}: {
  titulo: string;
  testid: string;
  itens: ItemContratado[];
  vazio: string;
  /** Para o recurso dizer de qual módulo é. */
  modulos?: ItemContratado[];
}) {
  const nomeDoModulo = (chave: string | null) =>
    chave ? modulos?.find((modulo) => modulo.chave === chave)?.nome : undefined;
  return (
    <Cartao titulo={titulo} data-testid={testid}>
      {itens.length === 0 ? (
        <p className="cartao__texto">{vazio}</p>
      ) : (
        <Tabela legenda={titulo}>
          <Corpo>
            {itens.map((item) => {
              const situacao = situacaoDoItem(item.situacao);
              const origem = nomeDoModulo(item.modulo_chave);
              return (
                <Linha key={item.chave} data-testid={`item-${item.chave}`}>
                  <Celula>
                    {item.nome}
                    {origem ? <div className="celula-secundaria">{origem}</div> : null}
                  </Celula>
                  <Celula className="text-right">
                    <Chip estado={situacao.tom}>{situacao.rotulo}</Chip>
                  </Celula>
                </Linha>
              );
            })}
          </Corpo>
        </Tabela>
      )}
    </Cartao>
  );
}

function Empresa({ dados }: { dados: DadosDoContrato }) {
  const { empresa, consultor } = dados;
  return (
    <Cartao titulo="Dados da sua empresa" className="mb-3" data-testid="dados-da-empresa">
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 m-0">
        <Dado rotulo="Razão social">{empresa.razao_social}</Dado>
        <Dado rotulo="Nome fantasia">{empresa.nome_fantasia ?? "—"}</Dado>
        <Dado rotulo="CNPJ">
          <span className="numero-tabular">{formatarCnpj(empresa.cnpj)}</span>
        </Dado>
        <Dado rotulo="Cidade">{empresa.cidade ? `${empresa.cidade}/${empresa.uf ?? ""}` : "—"}</Dado>
        <Dado rotulo="Segmento">{empresa.segmento ?? "—"}</Dado>
        <Dado rotulo="ERP">{empresa.erp ?? "Não informado"}</Dado>
        <Dado rotulo="Ambiente">{rotuloDoAmbiente(empresa.ambiente_tipo)}</Dado>
        <Dado rotulo="Consultor da conta">{consultor?.nome ?? "A definir pela GEOvendas"}</Dado>
      </dl>

      <h4 className="campo__rotulo mt-4">Endereços</h4>
      {empresa.enderecos.length === 0 ? (
        <p className="cartao__texto" data-testid="sem-endereco">
          Nenhum endereço cadastrado ainda.
        </p>
      ) : (
        <ul className="lista-de-marcas" data-testid="enderecos-da-empresa">
          {empresa.enderecos.map((endereco, indice) => (
            <li className="lista-de-marcas__item" key={`${endereco.rotulo}-${indice}`}>
              <b>{endereco.rotulo}</b>
              {endereco.principal ? <small className="dado-secundario"> · principal</small> : null}
              {linhasDoEndereco(endereco).map((linha) => (
                <span className="dado-secundario" key={linha}>
                  {linha}
                </span>
              ))}
            </li>
          ))}
        </ul>
      )}

      <p className="nota-rodape mt-3">
        Algum dado errado? Fale com {consultor ? <b>{consultor.nome}</b> : "a sua consultora"} — a
        correção é feita por nós e fica registrada.
      </p>
    </Cartao>
  );
}

function Documentos({ dados }: { dados: DadosDoContrato }) {
  const termos = useTermosDeEncerramento();
  const contrato = dados.contrato;
  return (
    <Cartao titulo="Documentos" data-testid="documentos-do-contrato">
      <Tabela legenda="Documentos do contrato">
        <Corpo>
          <Linha data-testid="documento-contrato-assinado">
            <Celula>
              <b>Contrato assinado</b>
              <div className="celula-secundaria">
                {contrato
                  ? `PDF · ${formatarTamanho(contrato.tamanho)} · ${
                      contrato.substituido_em
                        ? `substituído em ${formatarData(contrato.substituido_em)}`
                        : `anexado em ${formatarData(contrato.anexado_em)}`
                    }`
                  : "Ainda não anexado"}
              </div>
            </Celula>
            <Celula className="text-right">
              {contrato ? (
                <a
                  className="botao botao--pequeno"
                  href={CAMINHO_DO_ARQUIVO_DO_CONTRATO}
                  download
                  data-testid="baixar-contrato"
                >
                  Baixar o contrato assinado
                </a>
              ) : (
                <span data-testid="contrato-nao-anexado">Contrato não anexado</span>
              )}
            </Celula>
          </Linha>
        </Corpo>
      </Tabela>

      <h4 className="campo__rotulo mt-4">Termos de encerramento</h4>
      {termos.isPending ? (
        <p className="cartao__texto" role="status">
          Conferindo os termos…
        </p>
      ) : termos.isError ? (
        <p className="cartao__texto" data-testid="falha-dos-termos">
          Não foi possível conferir os termos de encerramento agora. O contrato continua
          disponível acima.
        </p>
      ) : termos.data.length === 0 ? (
        <p className="cartao__texto" data-testid="sem-termos">
          Nenhum módulo repassado ainda. Cada módulo repassado ao atendimento gera um termo de
          encerramento, e ele fica aqui.
        </p>
      ) : (
        <Tabela legenda="Termos de encerramento" data-testid="termos-de-encerramento">
          <Corpo>
            {termos.data.map((termo) => (
              <Linha key={termo.id} data-testid={`termo-${termo.id}`}>
                <Celula>
                  <b>Termo de encerramento — {termo.modulo_nome}</b>
                  <div className="celula-secundaria">
                    PDF · {formatarTamanho(termo.tamanho)} · gerado em{" "}
                    {formatarData(termo.gerado_em)}
                  </div>
                </Celula>
                <Celula className="text-right">
                  <a
                    className="botao botao--pequeno"
                    href={caminhoDoTermoDeEncerramento(termo.id)}
                    download
                  >
                    Baixar<span className="sr-only"> o termo de {termo.modulo_nome}</span>
                  </a>
                </Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      )}
    </Cartao>
  );
}
